// src/components/BookCover/CheckpointTimelineModal.jsx
import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import * as fabric from 'fabric';
import { API_BASE } from '../../utils/api';
import { useToast } from '../../context/ToastContext';
import '../../styles/CheckpointTimelineModal.css';

// Render a Fabric JSON design to a PNG data URL using an offscreen canvas.
// `multiplier` scales the exported image (use < 1 for thumbnails).
async function renderDesignToImage(designJson, width, height, multiplier = 1) {
  let offCanvas = null;
  try {
    const offscreenEl = document.createElement('canvas');
    offscreenEl.width = width;
    offscreenEl.height = height;
    offCanvas = new fabric.Canvas(offscreenEl, {
      width,
      height,
      backgroundColor: '#ffffff',
      renderOnAddRemove: false,
    });
    const parsed = typeof designJson === 'string' ? JSON.parse(designJson) : designJson;
    await offCanvas.loadFromJSON(parsed);
    offCanvas.backgroundColor = parsed?.backgroundColor || parsed?.background || '#ffffff';
    offCanvas.renderAll();
    return offCanvas.toDataURL({ format: 'png', multiplier });
  } catch {
    return null;
  } finally {
    if (offCanvas) {
      try { offCanvas.dispose(); } catch { /* ignore */ }
    }
  }
}

/**
 * CheckpointTimelineModal
 * -----------------------
 * A full-screen modal showing the checkpoint timeline with:
 *  - Selectable checkpoint list (newest → oldest)
 *  - Restore button (restores selected checkpoint, auto-saves current first)
 *  - Create New Version From This (creates a new cover version from checkpoint design)
 *  - Compare with Current (side-by-side snapshot comparison)
 *  - Cancel
 *
 * Props:
 *  - draftId, versionId         – IDs for API calls
 *  - checkpoints                – array of { id, label, savedByName, savedByRole, createdAt, isAutoSave }
 *  - onClose                    – close modal
 *  - onRestored(designSnapshot) – called after restore succeeds (parent reloads canvas)
 *  - onCheckpointsChanged(list) – called when the checkpoint list changes (after restore creates auto-save)
 *  - canvas                     – Fabric.js canvas ref (for current snapshot in Compare view)
 *  - faceDims                   – { faceW, faceH, spineW }
 *  - forceSave                  – parent's forceSave function
 */
export default function CheckpointTimelineModal({
  draftId,
  versionId,
  checkpoints,
  onClose,
  onRestored,
  onCheckpointsChanged,
  canvas,
  faceDims,
  forceSave,
}) {
  const toast = useToast();
  const [selectedId, setSelectedId] = useState(null);
  const [busy, setBusy] = useState(false);       // generic busy flag
  const [busyAction, setBusyAction] = useState(null); // 'restore' | 'version' | 'compare' | 'delete'
  const [compareData, setCompareData] = useState(null); // { currentImg, checkpointImg }
  const [thumbnails, setThumbnails] = useState({}); // { [checkpointId]: dataUrl }
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const compareRef = useRef(null);

  const token = localStorage.getItem('rv_access_token');
  const headers = useMemo(() => ({
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  }), [token]);

  const selected = checkpoints.find(cp => cp.id === selectedId);

  // ── Restore ────────────────────────────────────────────────
  const handleRestore = useCallback(async () => {
    if (!selectedId) return;
    setBusy(true);
    setBusyAction('restore');
    try {
      // Force-save before restore so nothing is lost
      if (forceSave) await forceSave(false);

      const res = await fetch(
        `${API_BASE}/api/drafts/${draftId}/cover/versions/${versionId}/checkpoints/${selectedId}/restore`,
        { method: 'POST', credentials: 'omit', headers },
      );
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        toast.error(err.error || 'Restore failed.');
        return;
      }
      const data = await res.json();

      if (data.skipped) {
        toast.info(data.message || 'Design already matches this checkpoint.');
        return;
      }

      // Refresh checkpoint list (restore creates an auto-save checkpoint)
      const listRes = await fetch(
        `${API_BASE}/api/drafts/${draftId}/cover/versions/${versionId}/checkpoints`,
        { credentials: 'omit', headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) } },
      );
      if (listRes.ok) {
        const ld = await listRes.json();
        if (onCheckpointsChanged) onCheckpointsChanged(ld.checkpoints || []);
      }

      // Tell parent to reload canvas
      const snap = data.restoredDesign || {};
      if (onRestored) onRestored(snap);
      onClose();
    } catch (err) {
      console.error('Restore failed:', err);
      toast.error('Failed to restore checkpoint.');
    } finally {
      setBusy(false);
      setBusyAction(null);
    }
  }, [selectedId, draftId, versionId, headers, token, forceSave, onRestored, onCheckpointsChanged, onClose, toast]);

  // ── Create New Version From This ───────────────────────────
  const handleCreateVersion = useCallback(async () => {
    if (!selectedId || !selected) return;
    setBusy(true);
    setBusyAction('version');
    try {
      const res = await fetch(
        `${API_BASE}/api/drafts/${draftId}/cover/versions/${versionId}/checkpoints/${selectedId}/create-version`,
        { method: 'POST', credentials: 'omit', headers },
      );
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        toast.error(err.error || 'Failed to create version.');
        return;
      }
      const data = await res.json();
      const newVersionId = data.version?.id;
      if (newVersionId) {
        // Force a full page load so the canvas setup hook re-initialises
        // with the new version. A client-side navigate() keeps the designer
        // component mounted and its canvas gets disposed without being
        // recreated (didInit guard), which crashes the page.
        onClose();
        window.location.assign(`/bookcover/${draftId}/designer/${newVersionId}`);
      }
    } catch (err) {
      console.error('Create version from checkpoint failed:', err);
      toast.error('Failed to create version from checkpoint.');
    } finally {
      setBusy(false);
      setBusyAction(null);
    }
  }, [selectedId, selected, draftId, versionId, headers, onClose, toast]);

  // ── Compare with Current ───────────────────────────────────
  const handleCompare = useCallback(async () => {
    if (!selectedId || !canvas) return;
    setBusy(true);
    setBusyAction('compare');
    try {
      // Capture current canvas as a snapshot
      const { faceW, faceH, spineW } = faceDims;
      const totalW = faceW * 2 + spineW;
      const savedDims = { width: canvas.getWidth(), height: canvas.getHeight() };
      const savedVpt = [...canvas.viewportTransform];

      canvas.setDimensions({ width: totalW, height: faceH });
      canvas.viewportTransform = [1, 0, 0, 1, 0, 0];
      if (!canvas.backgroundColor || canvas.backgroundColor === 'transparent') {
        canvas.backgroundColor = '#ffffff';
      }
      canvas.discardActiveObject();
      canvas.requestRenderAll();

      const currentImg = canvas.toDataURL({ format: 'png', multiplier: 1 });

      // Restore canvas dimensions
      canvas.setDimensions(savedDims);
      canvas.viewportTransform = savedVpt;
      canvas.requestRenderAll();

      // Fetch checkpoint design and render it on an offscreen canvas
      const res = await fetch(
        `${API_BASE}/api/drafts/${draftId}/cover/versions/${versionId}/checkpoints/${selectedId}/snapshot`,
        { credentials: 'omit', headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) } },
      );

      let checkpointImg = null;
      if (res.ok) {
        const data = await res.json();
        checkpointImg = data.snapshotUrl || null;

        // If server returned design JSON instead of image, render it offscreen
        if (!checkpointImg && data.design) {
          checkpointImg = await renderDesignToImage(data.design, totalW, faceH);
        }
      }

      setCompareData({ currentImg, checkpointImg });
    } catch (err) {
      console.error('Compare failed:', err);
      toast.error('Failed to generate comparison.');
    } finally {
      setBusy(false);
      setBusyAction(null);
    }
  }, [selectedId, canvas, faceDims, draftId, versionId, token, toast]);

  // Close compare on Escape
  useEffect(() => {
    if (!compareData) return;
    const handler = (e) => { if (e.key === 'Escape') setCompareData(null); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [compareData]);

  // ── Delete the currently selected checkpoint ───────────────
  const handleDeleteSelected = useCallback(async () => {
    if (!selectedId) return;
    setBusy(true);
    setBusyAction('delete');
    setShowDeleteConfirm(false);
    try {
      const res = await fetch(
        `${API_BASE}/api/drafts/${draftId}/cover/versions/${versionId}/checkpoints/${selectedId}`,
        { method: 'DELETE', credentials: 'omit', headers },
      );
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        toast.error(err.error || 'Failed to delete checkpoint.');
        return;
      }
      const remaining = checkpoints.filter(cp => cp.id !== selectedId);
      if (onCheckpointsChanged) onCheckpointsChanged(remaining);
      setSelectedId(null);
      toast.success('Checkpoint deleted.');
    } catch (err) {
      console.error('Delete failed:', err);
      toast.error('Failed to delete checkpoint.');
    } finally {
      setBusy(false);
      setBusyAction(null);
    }
  }, [selectedId, draftId, versionId, headers, checkpoints, onCheckpointsChanged, toast]);

  // Generate small thumbnails for each checkpoint on mount / when the list
  // changes. Each checkpoint's design JSON is fetched and rendered offscreen
  // at a reduced multiplier so the preview stays lightweight.
  useEffect(() => {
    if (!checkpoints || checkpoints.length === 0) return;
    const { faceW, faceH, spineW } = faceDims || {};
    if (!faceW || !faceH || !spineW) return;

    const totalW = faceW * 2 + spineW;
    const THUMB_H = 56;
    const multiplier = THUMB_H / faceH;

    let cancelled = false;
    const authHeaders = { ...(token ? { Authorization: `Bearer ${token}` } : {}) };

    const missing = checkpoints.filter(cp => !thumbnails[cp.id]);
    if (missing.length === 0) return;

    (async () => {
      // Render one at a time to avoid spiking the main thread with many
      // concurrent fabric canvases.
      for (const cp of missing) {
        if (cancelled) return;
        try {
          const res = await fetch(
            `${API_BASE}/api/drafts/${draftId}/cover/versions/${versionId}/checkpoints/${cp.id}/snapshot`,
            { credentials: 'omit', headers: authHeaders },
          );
          if (!res.ok) continue;
          const data = await res.json();
          if (!data.design) continue;
          const url = await renderDesignToImage(data.design, totalW, faceH, multiplier);
          if (!cancelled && url) {
            setThumbnails(prev => ({ ...prev, [cp.id]: url }));
          }
        } catch {
          /* swallow — card just shows placeholder */
        }
      }
    })();

    return () => { cancelled = true; };
    // thumbnails intentionally omitted — we only want to (re)run on list changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkpoints, draftId, versionId, faceDims, token]);

  // ── Compare overlay ────────────────────────────────────────
  if (compareData) {
    return (
      <div className="cpt-overlay" onClick={() => setCompareData(null)}>
        <div className="cpt-compare-modal" onClick={e => e.stopPropagation()} ref={compareRef}>
          <div className="cpt-compare-header">
            <h3>Compare with Current</h3>
            <button className="cpt-close-btn" onClick={() => setCompareData(null)}>✕</button>
          </div>
          <div className="cpt-compare-body">
            <div className="cpt-compare-panel">
              <div className="cpt-compare-label">Current Design</div>
              {compareData.currentImg
                ? <img src={compareData.currentImg} alt="Current design" className="cpt-compare-img" />
                : <div className="cpt-compare-empty">Unable to capture</div>}
            </div>
            <div className="cpt-compare-divider" />
            <div className="cpt-compare-panel">
              <div className="cpt-compare-label">Checkpoint: {selected?.label || 'Selected'}</div>
              {compareData.checkpointImg
                ? <img src={compareData.checkpointImg} alt="Checkpoint design" className="cpt-compare-img" />
                : <div className="cpt-compare-empty">Snapshot not available</div>}
            </div>
          </div>
          <div className="cpt-compare-footer">
            <button className="cpt-btn cpt-btn--secondary" onClick={() => setCompareData(null)}>Close</button>
          </div>
        </div>
      </div>
    );
  }

  // ── Main timeline modal ────────────────────────────────────
  return (
    <div className="cpt-overlay" onClick={onClose}>
      <div className="cpt-modal" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="cpt-header">
          <div className="cpt-header-left">
            <span className="cpt-header-icon">🕑</span>
            <h3>Checkpoint Timeline</h3>
            <span className="cpt-header-count">{checkpoints.length} of 10 checkpoint{checkpoints.length !== 1 ? 's' : ''}</span>
          </div>
          <button className="cpt-close-btn" onClick={onClose}>✕</button>
        </div>

        {/* Checkpoint list */}
        <div className="cpt-list">
          {checkpoints.length === 0 && (
            <div className="cpt-empty">No checkpoints yet. Save one to start tracking your history.</div>
          )}
          {checkpoints.map(cp => (
            <div
              key={cp.id}
              className={[
                'cpt-card',
                cp.isAutoSave ? 'cpt-card--auto' : '',
                selectedId === cp.id ? 'cpt-card--selected' : '',
              ].join(' ')}
              onClick={() => setSelectedId(prev => prev === cp.id ? null : cp.id)}
            >
              <div className="cpt-card-radio">
                <span className={`cpt-radio ${selectedId === cp.id ? 'cpt-radio--active' : ''}`} />
              </div>
              <div className="cpt-card-thumb">
                {thumbnails[cp.id]
                  ? <img src={thumbnails[cp.id]} alt="" className="cpt-card-thumb-img" />
                  : <div className="cpt-card-thumb-placeholder">…</div>}
              </div>
              <div className="cpt-card-content">
                <div className="cpt-card-header">
                  <span className="cpt-card-icon">{cp.isAutoSave ? '🔄' : '📌'}</span>
                  <span className="cpt-card-label">{cp.label}</span>
                </div>
                <div className="cpt-card-meta">
                  <span className="cpt-card-user">{cp.savedByName}</span>
                  <span className={`cpt-card-role cpt-role--${cp.savedByRole}`}>{cp.savedByRole}</span>
                  <span className="cpt-card-time">
                    {cp.createdAt
                      ? new Date(cp.createdAt).toLocaleString(undefined, {
                          month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
                        })
                      : ''}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Action buttons */}
        <div className="cpt-actions">
          <button
            className="cpt-btn cpt-btn--primary"
            disabled={!selectedId || busy}
            onClick={handleRestore}
          >
            {busyAction === 'restore' ? '⏳ Restoring...' : '↩ Restore'}
          </button>
          <button
            className="cpt-btn cpt-btn--accent"
            disabled={!selectedId || busy}
            onClick={handleCreateVersion}
          >
            {busyAction === 'version' ? '⏳ Creating...' : '🔀 Create New Version From This'}
          </button>
          <button
            className="cpt-btn cpt-btn--outline"
            disabled={!selectedId || busy}
            onClick={handleCompare}
          >
            {busyAction === 'compare' ? '⏳ Loading...' : '⚖ Compare with Current'}
          </button>
          <button
            className="cpt-btn cpt-btn--danger"
            disabled={!selectedId || busy}
            onClick={() => setShowDeleteConfirm(true)}
          >
            {busyAction === 'delete' ? '⏳ Deleting...' : '🗑 Delete'}
          </button>
          <button className="cpt-btn cpt-btn--secondary" onClick={onClose} disabled={busy}>
            Cancel
          </button>
        </div>

        {showDeleteConfirm && selected && (
          <div
            className="cpt-overlay cpt-overlay--nested"
            onClick={() => setShowDeleteConfirm(false)}
          >
            <div className="cpt-confirm-modal" onClick={e => e.stopPropagation()}>
              <h3>Delete this checkpoint?</h3>
              <p className="cpt-confirm-target">
                {selected.isAutoSave ? '🔄 ' : '📌 '}{selected.label}
              </p>
              <p className="cpt-confirm-warning">
                ⚠ This will permanently remove the checkpoint. You will no longer be able to
                restore or create new versions from it. This action cannot be undone.
              </p>
              <div className="cpt-confirm-actions">
                <button
                  className="cpt-btn cpt-btn--secondary"
                  onClick={() => setShowDeleteConfirm(false)}
                >
                  Cancel
                </button>
                <button
                  className="cpt-btn cpt-btn--danger"
                  onClick={handleDeleteSelected}
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
